"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, Expand, Minimize2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { MobileWeekView } from "@/components/mobile-week-view"
import { DesktopMonthView } from "@/components/desktop-month-view"
import { DayDetailSheet } from "@/components/day-detail-sheet"
import { StreakBadge } from "@/components/streak-badge"
import type { DailyEntry } from "@/lib/db/schema"
import {
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns"
import {
  formatCalendarHeader,
  calculateCurrentStreak,
} from "@/lib/date-utils"
import { cn } from "@/lib/utils"

interface AdaptiveCalendarViewProps {
  entries: DailyEntry[]
  currentStreak?: number
  todayEntry?: DailyEntry
  className?: string
}

type ViewMode = "week" | "month"

/**
 * AdaptiveCalendarView - Container component that adapts between mobile and desktop views
 *
 * Features:
 * - Mobile: 7-day week view by default with expand option
 * - Desktop: Can be toggled between week and month views
 * - Smooth animations between view transitions
 * - Integrated streak badge
 * - Navigation controls for week/month
 * - Day detail sheet on click
 */
export function AdaptiveCalendarView({
  entries,
  currentStreak: currentStreakProp,
  todayEntry,
  className,
}: AdaptiveCalendarViewProps) {
  const [centerDate, setCenterDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>("week")
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)

  // Use provided streak or calculate from entries as fallback
  const currentStreak = currentStreakProp ?? calculateCurrentStreak(entries)

  // Navigation handlers
  const handlePrevious = () => {
    if (viewMode === "week") {
      setCenterDate((prev) => subWeeks(prev, 1))
    } else {
      setCenterDate((prev) => subMonths(prev, 1))
    }
  }

  const handleNext = () => {
    if (viewMode === "week") {
      setCenterDate((prev) => addWeeks(prev, 1))
    } else {
      setCenterDate((prev) => addMonths(prev, 1))
    }
  }

  const handleToday = () => {
    setCenterDate(new Date())
  }

  const handleWeekChange = (newDate: Date) => {
    setCenterDate(newDate)
  }

  const handleDayClick = (date: Date) => {
    setSelectedDate(date)
    setDetailSheetOpen(true)
  }

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "week" ? "month" : "week"))
  }

  return (
    <div className={cn("w-full space-y-4", className)}>
      {/* Header with streak badge and navigation */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
        {/* Streak badge */}
        <div className="flex-shrink-0">
          <StreakBadge streak={currentStreak} inThreat={currentStreak > 0 && !todayEntry} />
        </div>

        {/* Navigation controls */}
        <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            aria-label="Předchozí"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleToday}
            className="min-w-[100px] font-semibold"
          >
            {formatCalendarHeader(centerDate)}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            aria-label="Další"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* View mode toggle (desktop only) */}
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            className="hidden sm:flex"
            aria-label={viewMode === "week" ? "Zobrazit měsíc" : "Zobrazit týden"}
          >
            {viewMode === "week" ? (
              <Expand className="h-4 w-4" />
            ) : (
              <Minimize2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Calendar view */}
      <AnimatePresence mode="wait">
        {viewMode === "week" ? (
          <motion.div
            key="week-view"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <MobileWeekView
              entries={entries}
              centerDate={centerDate}
              onDayClick={handleDayClick}
              onWeekChange={handleWeekChange}
            />
          </motion.div>
        ) : (
          <motion.div
            key="month-view"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <DesktopMonthView
              entries={entries}
              currentDate={centerDate}
              onDayClick={handleDayClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Day detail sheet */}
      <DayDetailSheet
        date={selectedDate}
        entries={entries}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </div>
  )
}
